import {
  column,
  CompoundField,
  ContentHeader,
  Css,
  emptyCell,
  GridCellContent,
  GridColumn,
  GridSortConfig,
  GridTableLayout,
  numericColumn,
  RowStyles,
  SelectField,
  simpleDataRows,
  simpleHeader,
  SimpleHeaderAndData,
  Tag,
  useGridTableApi,
  useGridTableLayoutState,
} from "@homebound/beam";
import { Dispatch, SetStateAction, useCallback, useMemo, useState } from "react";
import { formatToPrice, Loading } from "src/components";
import { NamedFragment } from "src/fragments/commonFragments.generated";
import { EmptyState } from "src/routes/components/EmptyState";
import { EmptyPlanAndOptionImage } from "src/routes/developments/plan-and-options/components/EmptyStateSvgs";
import { useProjectContext } from "src/routes/projects/context/ProjectContext";
import { formatNumberToString } from "src/utils";
import { JsonParam, StringParam, useQueryParams } from "use-query-params";
import {
  ConfigurationComparison_CostConfigurationSummaryFragment,
  ConfigurationComparisonQuery,
  useConfigurationComparisonCombinationsQuery,
  useConfigurationComparisonDimensionsQuery,
  useConfigurationComparisonOptionsQuery,
  useConfigurationComparisonQuery,
} from "./ConfigurationComparisonTab.generated";
import { useOpenCostCategoryModal } from "./components/CostCategoryModal";
import { ReloadCostsBanner } from "./components/ReloadCostsBanner";
import {
  COMPARISON_COMBINATIONS_LIMIT,
  COMPARISON_PAGE_SIZE,
  ComparisonDimension,
  ComparisonFilter,
  ComparisonPicks,
  ComparisonRow,
  createComparisonRows,
  createMetricColumns,
  createOfferingFilterDefs,
  deltaPercent,
  ResolvedSelection,
  resolveSelection,
  selectedOfferingIds,
  toComparisonFilter,
  typeDimensions,
} from "./utils";

type ConfigurationComparisonTabProps = { productOfferings: NamedFragment[] };

export function ConfigurationComparisonTab({ productOfferings }: ConfigurationComparisonTabProps) {
  const { id: projectId } = useProjectContext();
  const api = useGridTableApi<Row>();
  const openDefinitions = useOpenCostCategoryModal();
  const productOfferingIds = useMemo(() => productOfferings.map((po) => po.id), [productOfferings]);
  // The dimensions and their values
  const dimensionsQuery = useConfigurationComparisonDimensionsQuery({
    variables: { productOfferingIds },
    skip: productOfferingIds.isEmpty,
  });
  const dimensions = useMemo(
    () => typeDimensions(dimensionsQuery.data?.potentialBidPlanDimensions ?? []),
    [dimensionsQuery.data],
  );
  // The offerings' options in those dimensions, to sort the values in checkout order
  const optionsQuery = useConfigurationComparisonOptionsQuery({
    variables: { productOfferingIds, typeIds: dimensions.map((d) => d.type.id) },
    skip: dimensions.isEmpty,
  });
  const offeringOptions = optionsQuery.data?.readyPlanOptions;
  // The valid combinations: the summaries job writes a summary per combination `generateValidCombinations` allows, so
  // loading them all saves re-deriving the option rules (e.g. prerequisites) here.
  const combinationsQuery = useConfigurationComparisonCombinationsQuery({
    variables: { filter: { productOfferings: productOfferingIds }, page: { limit: COMPARISON_COMBINATIONS_LIMIT } },
    skip: productOfferingIds.isEmpty,
    fetchPolicy: "no-cache",
  });
  const combinations = useMemo(
    () => combinationsQuery.data?.costConfigurationSummaries.entities ?? [],
    [combinationsQuery.data],
  );
  // All Configurations links here with a configuration's spec level as the baseline and its other values fixed
  const [{ baseline: baselineParam, fixed: fixedParam }] = useQueryParams({ baseline: StringParam, fixed: JsonParam });
  const [picks, setPicks] = useState<ComparisonPicks>({
    baselineId: baselineParam ?? undefined,
    fixed: fixedParam ?? {},
  });
  // The Product Offering filter, persisted per project
  const filterDefs = useMemo(() => createOfferingFilterDefs(productOfferings), [productOfferings]);
  const layoutState = useGridTableLayoutState<ComparisonFilter>({
    persistedFilter: { filterDefs, storageKey: `${projectId}_costAnalysisConfigurationComparisonFilter` },
    search: "client",
  });
  const { productOffering: offeringFilter } = layoutState.filter;
  // The compare dimension, baseline and fixed values, defaulting any that's unpicked or no longer available
  const selection = useMemo(() => {
    if (!offeringOptions) return undefined;
    const selected = selectedOfferingIds(offeringFilter, productOfferingIds);
    const visible = combinations.filter((c) => selected.includes(c.productOffering.id));
    return resolveSelection(picks, dimensions, offeringOptions, visible);
  }, [picks, dimensions, offeringOptions, combinations, offeringFilter, productOfferingIds]);
  // The rows: the filtered offerings' configurations at the fixed values, for every compare value
  const filter = selection?.baseline && toComparisonFilter(selection, offeringFilter, productOfferingIds);
  const query = useConfigurationComparisonQuery({
    variables: { filter, page: { limit: COMPARISON_PAGE_SIZE } },
    skip: !filter,
    fetchPolicy: "no-cache",
  });
  const columns = useMemo(
    () => createColumns(selection?.compare.displayName ?? "", openDefinitions),
    [selection?.compare.displayName, openDefinitions],
  );
  // Per offering, the baseline row first, then its other compare values in checkout order
  const createRows = useCallback(
    (data: ConfigurationComparisonQuery | undefined) => [
      simpleHeader,
      ...simpleDataRows(selection && createComparisonRows(data?.costConfigurationSummaries.entities ?? [], selection)),
    ],
    [selection],
  );
  if (dimensionsQuery.loading || optionsQuery.loading || combinationsQuery.loading) return <Loading />;
  if (!selection || combinations.isEmpty) {
    return <EmptyState message={fallbackMessage} svg={<EmptyPlanAndOptionImage />} />;
  }
  return (
    <>
      <ReloadCostsBanner
        productOfferingIds={productOfferingIds}
        // The new costs can add combinations, so the pickers reload with the rows
        onReload={() => Promise.all([combinationsQuery.refetch(), query.refetch()])}
      />
      <ContentHeader
        title="Configuration Comparison"
        description="Compare each configuration against a baseline within this community. Costs recalculate automatically whenever a cost-changing edit is saved."
        actions={[
          {
            label: "Export",
            icon: "download",
            variant: "secondary",
            onClick: () => api.downloadToCsv("configuration-comparison"),
          },
        ]}
      />
      <ComparisonControls dimensions={dimensions} selection={selection} setPicks={setPicks} />
      <GridTableLayout
        layoutState={layoutState}
        emptyFallback={fallbackMessage}
        tableProps={{ api, query, createRows, columns, sorting, rowStyles, as: "virtual" }}
      />
    </>
  );
}

type ComparisonControlsProps = {
  dimensions: ComparisonDimension[];
  selection: ResolvedSelection;
  setPicks: Dispatch<SetStateAction<ComparisonPicks>>;
};

function ComparisonControls({ dimensions, selection, setPicks }: ComparisonControlsProps) {
  const { compare, compareValues, baseline, fixed } = selection;
  return (
    <div css={Css.layoutContainer.$}>
      <div css={Css.ba.bcGray200.br8.mx3.p3.df.fdc.gap3.mt3.$}>
        <div css={Css.df.fdc.aifs.gap1.$}>
          <div css={Css.smSb.$}>Compare</div>
          <div css={Css.xs.$}>Pick the dimension to compare and the baseline it's measured against.</div>
          <CompoundField>
            <SelectField
              labelStyle="inline"
              sizeToContent
              autoSort={false}
              label="Compare"
              options={dimensions.map((d) => ({ id: d.type.id, name: d.displayName }))}
              value={compare.type.id}
              // The baseline and the fixed values re-default for the new dimension
              onSelect={(compareTypeId) => compareTypeId && setPicks({ compareTypeId, fixed: {} })}
            />
            <SelectField
              labelStyle="inline"
              sizeToContent
              autoSort={false}
              label="Baseline"
              options={compareValues}
              value={baseline?.id}
              // The fixed values re-default for the new baseline
              onSelect={(baselineId) => baselineId && setPicks((p) => ({ ...p, baselineId, fixed: {} }))}
            />
          </CompoundField>
        </div>
        <div css={Css.df.fdc.gap1.$}>
          <div css={Css.smSb.$}>Fixed</div>
          <div css={Css.xs.$}>Applied to every row. Not shown as columns.</div>
          <div css={Css.df.gap2.$}>
            {fixed.map(({ dimension, options, value }, i) => (
              <div key={dimension.type.id}>
                <SelectField
                  labelStyle="inline"
                  sizeToContent
                  autoSort={false}
                  label={dimension.displayName}
                  options={options}
                  value={value?.id}
                  // The fields to the right re-default, so only the picks to the left carry over
                  onSelect={(valueId) =>
                    setPicks((p) => ({
                      ...p,
                      fixed: {
                        ...fixed.slice(0, i).mapToObject((f) => [f.dimension.type.id, p.fixed[f.dimension.type.id]]),
                        [dimension.type.id]: valueId,
                      },
                    }))
                  }
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

type Row = SimpleHeaderAndData<ComparisonRow>;
type Metric = (summary: ConfigurationComparison_CostConfigurationSummaryFragment) => number;
const sorting: GridSortConfig = { on: "client", initial: ["productOffering", "ASC"] };
const rowStyles: RowStyles<Row> = { data: { cellCss: ({ data }) => (data.isBaseline ? Css.bgBlue50.xsSb.$ : {}) } };
const fallbackMessage = "There aren't any configurations to compare.";

function createColumns(compareHeader: string, openDefinitions: VoidFunction): GridColumn<Row>[] {
  return [
    column<Row>({
      id: "productOffering",
      name: "Product Offering",
      header: "Product Offering",
      canHide: false,
      data: ({ summary }) => summary.productOffering.name,
      mw: "200px",
      sticky: "left",
    }),
    column<Row>({
      id: "compareValue",
      name: compareHeader,
      header: compareHeader,
      canHide: false,
      clientSideSort: false,
      data: ({ compareValue, isBaseline }) => ({
        content: () => (
          <div css={Css.df.aic.gap1.$}>
            {compareValue}
            {isBaseline && <Tag text="Baseline" type="neutral" />}
          </div>
        ),
        value: compareValue,
      }),
      mw: "160px",
    }),
    ...createMetricColumns<ComparisonRow>((row) => row.summary, false, openDefinitions),
    deltaColumn("deltaCostInCents", "Δ Cost $", (row) => deltaCell(row, (s) => s.totalCostInCents), false),
    deltaColumn(
      "deltaCostPerSellableSqftInCents",
      "Δ Cost / Sq Ft",
      (row) => deltaCell(row, (s) => s.costPerSellableSqftInCents),
      false,
    ),
    deltaColumn("deltaCostPercentage", "Δ Cost %", (row) => deltaPercentCell(row, (s) => s.totalCostInCents), false),
    deltaColumn("deltaPriceInCents", "Δ Price $", (row) => deltaCell(row, (s) => s.totalPriceInCents)),
    deltaColumn("deltaPricePerSellableSqftInCents", "Δ Price / Sq Ft", (row) =>
      deltaCell(row, (s) => s.pricePerSellableSqftInCents),
    ),
    deltaColumn("deltaPricePercentage", "Δ Price %", (row) => deltaPercentCell(row, (s) => s.totalPriceInCents)),
  ];
}

/** The deltas follow each offering's baseline row, so they never sort. */
function deltaColumn(id: string, name: string, data: (row: ComparisonRow) => GridCellContent, initHidden = true) {
  return numericColumn<Row>({
    id,
    name,
    header: name,
    canHide: true,
    initHidden,
    clientSideSort: false,
    mw: "120px",
    data,
  });
}

/** The row's metric less its baseline's, signed. The baseline row has nothing to compare against. */
function deltaCell({ summary, baseline, isBaseline }: ComparisonRow, metric: Metric): GridCellContent {
  if (isBaseline) return emptyCell;
  return { content: formatToPrice({ valueInCents: metric(summary) - metric(baseline), displayDirection: true }) };
}

function deltaPercentCell({ summary, baseline, isBaseline }: ComparisonRow, metric: Metric): GridCellContent {
  const percent = isBaseline ? undefined : deltaPercent(metric(summary), metric(baseline));
  if (percent === undefined) return emptyCell;
  return { content: `${percent > 0 ? "+" : ""}${formatNumberToString(percent, false, 1)}%` };
}
